import { chainflipAssets } from '@chainflip/utils/chainflip';
import { brokerAliasMap } from '@chainflip/utils/consts';
import { isNumber } from '@chainflip/utils/guard';
import { type ParsedUrlQuery } from 'querystring';
import { NUMBER_REGEX } from '@/shared/utils/regex';
import {
  formatDateForUrl,
  getQueryParams,
  parseAll,
  parseEndDate,
  parseStartDate,
  type QueryParser,
} from 'packages/block-explorer/helpers/queryFilters';
import { MAX_SWAP_SIZE } from '../components/SwapsPage/AllSwapsFilter/FilterSelections';
import {
  allSwapsInitialData as initialData,
  type AllSwapsStoreData as StoreData,
} from '../hooks/useFilterStores';

const SWAP_STATUSES = [
  'IN_PROGRESS',
  'PARTIAL_REFUND',
  'REFUND_SUCCESS',
  'SUCCESS',
  'SWAP_FAILED',
] as const;

const SWAP_TYPES = ['isBoosted', 'isDca', 'isCcm', 'isOnChain', 'isVaultSwap'] as const;

const parseValueUsd: QueryParser<number> = (value) =>
  value && Number(value) >= 0 && Number(value) <= MAX_SWAP_SIZE ? Number(value) : undefined;

const parseAddress: QueryParser<string> = (value) =>
  value && /^[0-9a-z]*$/i.test(value) ? value : undefined;

const parserMap: {
  [K in keyof StoreData]: (query: ParsedUrlQuery) => StoreData[K];
} = {
  startDate: (q) => parseStartDate(getQueryParams(q.startDate).single) ?? initialData.startDate,
  endDate: (q) => parseEndDate(getQueryParams(q.endDate).single) ?? initialData.endDate,
  swapStates: (q) =>
    parseAll(getQueryParams(q.statuses).commaSeparated, SWAP_STATUSES) ?? initialData.swapStates,
  assetsFrom: (q) =>
    parseAll(getQueryParams(q.assetsFrom).commaSeparated, chainflipAssets) ??
    initialData.assetsFrom,
  brokers: (q) =>
    parseAll(
      getQueryParams(q.brokers).commaSeparated,
      Object.entries(brokerAliasMap).map(([id]) => id),
    ) ?? initialData.brokers,
  assetsTo: (q) =>
    parseAll(getQueryParams(q.assetsTo).commaSeparated, chainflipAssets) ?? initialData.assetsTo,
  refundAddress: (q) =>
    parseAddress(getQueryParams(q.refundAddress).single) ?? initialData.refundAddress,
  destinationAddress: (q) =>
    parseAddress(getQueryParams(q.destinationAddress).single) ?? initialData.destinationAddress,
  swapSizeRange: (q) => {
    const minValueUsd = parseValueUsd(getQueryParams(q.minValueUsd).single);
    const maxValueUsd = parseValueUsd(getQueryParams(q.maxValueUsd).single);
    return isNumber(minValueUsd) && isNumber(maxValueUsd)
      ? [minValueUsd, maxValueUsd]
      : initialData.swapSizeRange;
  },
  swapTypes: (q) => {
    const swapTypes = SWAP_TYPES.filter((type) => {
      const value = getQueryParams(q[type]).single;
      return value === 'true';
    });
    return swapTypes.length ? swapTypes : initialData.swapTypes;
  },
};

export const getFiltersFromUrl = (query: ParsedUrlQuery) =>
  Object.entries(parserMap).reduce(
    (curr, [key, parser]) => ({ ...curr, [key]: parser(query) }),
    {},
  ) as { [K in keyof StoreData]: StoreData[K] };

export const toQueryObject = (filters: StoreData) => {
  const query: Record<string, string> = {
    startDate: formatDateForUrl(filters.startDate),
    endDate: formatDateForUrl(filters.endDate),
  };

  if (filters.swapStates.length) query.statuses = filters.swapStates.join(',');
  if (filters.assetsFrom.length) query.assetsFrom = filters.assetsFrom.join(',');
  if (filters.assetsTo.length) query.assetsTo = filters.assetsTo.join(',');
  if (filters.brokers.length) query.brokers = filters.brokers.join(',');
  if (filters.swapTypes.length)
    filters.swapTypes.forEach((type) => {
      query[type] = 'true';
    });
  if (filters.refundAddress?.length) query.refundAddress = filters.refundAddress;
  if (filters.destinationAddress?.length) query.destinationAddress = filters.destinationAddress;

  const [minValueUsd, maxValueUsd] = filters.swapSizeRange;
  if (!(minValueUsd === 0 && maxValueUsd === 0)) {
    query.minValueUsd = minValueUsd.toString();
    query.maxValueUsd = maxValueUsd.toString();
  }

  return query;
};

export const extractSwapRequestId = (queryString: string) =>
  NUMBER_REGEX.test(queryString) ? queryString : null;
